import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./MyAppsFunctions.module.css";
import { tableCellClasses } from "@mui/material/TableCell";
import Paper from "@mui/material/Paper";
import CloseIcon from "@mui/icons-material/Close";
import { Edit } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  Box,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Divider,
} from "@mui/material";
import { Button } from "react-bootstrap";
import ToggleSwitch from "./ToggleSwitch";
import MyAppsDetail from "./MyAppsDetail";
import MyAppsOnetimeDetail from "./MyAppsOnetimeDetail";
import DomainDetail from "./DomainDetail.tsx";
import ToggleZohoSync from "./ToggleZohoSync";
import { STATUS_DATA } from "../../utils/statusData";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const REACT_APP_SERVER_URL = process.env.REACT_APP_SERVER_URL;
const statusData = STATUS_DATA;

const MyAppsFunctions = ({
  selectedCard,
  isSomethingChanged1,
  setIsSomethingChanged1,
  role,
}) => {
  const navigate = useNavigate();
  const isSuperAdmin = role === "super_admin";
  const [hiddenById, setHiddenById] = React.useState({});
  const toggleSubHidden = async (id, nextHidden) => {
    setHiddenById((prev) => ({ ...prev, [id]: nextHidden }));
    try {
      const res = await fetch(
        `${REACT_APP_SERVER_URL}/toggle_subscription_visibility`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, hidden: nextHidden }),
        },
      );
      if (!res.ok) throw new Error("toggle failed");
    } catch (e) {
      setHiddenById((prev) => ({ ...prev, [id]: !nextHidden }));
      console.log("[hide_for_members] sub toggle failed:", e?.message);
    }
  };
  const [pageLoaded, setPageLoaded] = React.useState(false);
  // Separate flag for "we're refetching a new page of subs" — first load of
  // a workspace still blanks (pageLoaded=false) because there's nothing to
  // show yet, but a page/pageSize change keeps the existing rows visible
  // and only dims the subs table + disables the pagination controls.
  const [subsRefetching, setSubsRefetching] = React.useState(false);
  const workspaceIdRef = React.useRef(null);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [errorSnackbarOpen, setErrorSnackbarOpen] = React.useState(null);
  const [myAppData, setMyAppData] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [subsTotal, setSubsTotal] = useState(0);
  // Subs pagination is URL-controlled (?subsPage=..&subsPageSize=..) so the
  // page position survives browser Back and can be shared. Loads that used to
  // take 30-60s on workspaces with many subs (2 sequential Stripe calls per
  // sub) now only fetch the current page's Stripe data.
  const [subsSearchParams, setSubsSearchParams] = useSearchParams();
  const ALLOWED_PAGE_SIZES = [5, 10, 25, 50];
  const rawSubsPage = parseInt(subsSearchParams.get("subsPage") || "1", 10);
  const rawSubsPageSize = parseInt(
    subsSearchParams.get("subsPageSize") || "10",
    10,
  );
  const subsPage = Number.isFinite(rawSubsPage) && rawSubsPage > 0 ? rawSubsPage : 1;
  const subsPageSize = ALLOWED_PAGE_SIZES.includes(rawSubsPageSize)
    ? rawSubsPageSize
    : 10;
  // Deep-link target from the admin ref-lookup (?highlightSub=<id>): the
  // backend pins this sub to position 0 of page 1 and we flash-highlight
  // the matching row for a few seconds so the eye lands on it.
  const rawHighlightSub = parseInt(
    subsSearchParams.get("highlightSub") || "",
    10,
  );
  const highlightSubId =
    Number.isFinite(rawHighlightSub) && rawHighlightSub > 0
      ? rawHighlightSub
      : null;
  const patchSubsParams = (patch) => {
    setSubsSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === "" || v == null) next.delete(k);
          else next.set(k, String(v));
        }
        return next;
      },
      { replace: true },
    );
  };
  const [selectedApp, setSelectedApp] = useState({});
  const [payId, setPayId] = useState({});
  const [payConfirmation, setPayConfirmation] = useState(false);
  const [editProvider, setEditProvider] = useState(false);
  const [editVendorRenewalDate, setEditVendorRenewalDate] = useState(false);
  const [vendorRenewalDate, setVendorRenewalDate] = useState("");
  const [editPriceValidity, setEditPriceValidity] = useState(false);
  const [priceValidityDate, setPriceValidityDate] = useState("");
  const [provider, setProvider] = useState("");
  // Providers registry — powers the dropdown. Loaded on demand when the
  // edit-provider dialog opens so we don't fetch on every workspace view.
  const [providerOptions, setProviderOptions] = useState([]);
  const [providerOptionsLoaded, setProviderOptionsLoaded] = useState(false);
  const [providerOptionsLoading, setProviderOptionsLoading] = useState(false);
  // "Create new" mini-dialog on top of the edit-provider dialog.
  const [createProviderOpen, setCreateProviderOpen] = useState(false);
  const [newProviderName, setNewProviderName] = useState("");
  const [creatingProvider, setCreatingProvider] = useState(false);
  const [selectdPS, setSelectedPS] = useState(null);
  const [isSomethingChanged, setIsSomethingChanged] = useState(false);

  const CREATE_NEW_PROVIDER = "__create_new_provider__";
  const MANAGE_PROVIDERS = "__manage_providers__";

  // Manage-providers modal state: opens from the same dropdown, lets the
  // admin rename typo duplicates (CloudGeneric vs CloudGenric etc). A
  // rename that collides with another existing name MERGES both — the
  // backend rewrites purchasesubscriptions.provider for every affected sub.
  const [manageProvidersOpen, setManageProvidersOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  const startRename = (row) => {
    setRenamingId(row.id);
    setRenameDraft(row.name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft("");
  };

  const submitRename = async () => {
    const id = renamingId;
    const newName = String(renameDraft || "").trim();
    if (!id || !newName) return;
    setRenameBusy(true);
    try {
      const res = await fetch(`${REACT_APP_SERVER_URL}/admin/providers/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ name: newName }),
      });
      const json = await res.json().catch(() => ({}));
      if (!json?.success) {
        setErrorMessage(json?.msg || "Rename failed");
        setErrorSnackbarOpen(true);
        return;
      }
      // Refresh the local list. If the operation was a merge, one row
      // vanishes and the surviving row keeps its id.
      await fetchProviders();
      // If the currently-selected sub's provider was renamed, keep the
      // Select in sync with the new canonical spelling.
      if (
        json.renamed_from &&
        provider &&
        provider.toLowerCase() === json.renamed_from.toLowerCase()
      ) {
        setProvider(json.data?.name || newName);
      }
      if (
        json.merged_from &&
        provider &&
        provider.toLowerCase() === json.merged_from.toLowerCase()
      ) {
        setProvider(json.data?.name || newName);
      }
      // Signal the parent so the list refetch picks up new provider strings.
      setIsSomethingChanged1(!isSomethingChanged1);
      cancelRename();
    } catch (e) {
      setErrorMessage(e?.message || "Rename failed");
      setErrorSnackbarOpen(true);
    } finally {
      setRenameBusy(false);
    }
  };

  const deleteProviderRow = async (row) => {
    if (
      !window.confirm(
        `Delete provider "${row.name}" from the dropdown? Blocked if any subscription still uses this name — in that case, rename or merge it first.`,
      )
    ) {
      return;
    }
    try {
      const res = await fetch(`${REACT_APP_SERVER_URL}/admin/providers/${row.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const json = await res.json().catch(() => ({}));
      if (!json?.success) {
        setErrorMessage(json?.msg || "Delete failed");
        setErrorSnackbarOpen(true);
        return;
      }
      await fetchProviders();
    } catch (e) {
      setErrorMessage(e?.message || "Delete failed");
      setErrorSnackbarOpen(true);
    }
  };

  const fetchProviders = React.useCallback(async () => {
    setProviderOptionsLoading(true);
    try {
      const res = await fetch(`${REACT_APP_SERVER_URL}/admin/providers`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      const json = await res.json().catch(() => ({}));
      if (json?.success && Array.isArray(json.data)) {
        setProviderOptions(json.data);
        setProviderOptionsLoaded(true);
      }
    } catch (e) {
      console.log("[providers] fetch failed:", e?.message || e);
    } finally {
      setProviderOptionsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (editProvider && !providerOptionsLoaded && !providerOptionsLoading) {
      fetchProviders();
    }
  }, [editProvider, providerOptionsLoaded, providerOptionsLoading, fetchProviders]);

  // Manage-providers modal reuses the same loader. Placed AFTER
  // fetchProviders is declared so the dep array is safe (an earlier
  // version had this above the declaration and hit TDZ on first render).
  React.useEffect(() => {
    if (manageProvidersOpen && !providerOptionsLoaded && !providerOptionsLoading) {
      fetchProviders();
    }
  }, [manageProvidersOpen, providerOptionsLoaded, providerOptionsLoading, fetchProviders]);

  const handleCreateProvider = async () => {
    const name = String(newProviderName || "").trim();
    if (!name) return;
    setCreatingProvider(true);
    try {
      const res = await fetch(`${REACT_APP_SERVER_URL}/admin/providers`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ name }),
      });
      const json = await res.json().catch(() => ({}));
      if (json?.success && json.data?.name) {
        // Add to the local list if it's new (backend may have returned
        // an existing row on case collision — canonical spelling wins).
        setProviderOptions((prev) => {
          if (prev.some((p) => p.id === json.data.id)) return prev;
          return [...prev, json.data].sort((a, b) =>
            String(a.name).toLowerCase().localeCompare(String(b.name).toLowerCase()),
          );
        });
        // Attach to the current subscription right away.
        setProvider(json.data.name);
        setCreateProviderOpen(false);
        setNewProviderName("");
      } else {
        setErrorMessage(json?.msg || "Failed to create provider");
        setErrorSnackbarOpen(true);
      }
    } catch (e) {
      setErrorMessage(e?.message || "Failed to create provider");
      setErrorSnackbarOpen(true);
    } finally {
      setCreatingProvider(false);
    }
  };
  const handleErrorSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setErrorSnackbarOpen(false);
  };

  useEffect(() => {
    // First fetch for THIS workspace? Full blank + spinner (nothing to show
    // yet). Subsequent fetches (page/pageSize/isSomethingChanged) keep the
    // workspace shell visible and only dim the subs table.
    const isFirstLoadForWorkspace =
      workspaceIdRef.current !== selectedCard?.id;
    if (isFirstLoadForWorkspace) {
      setPageLoaded(false);
      workspaceIdRef.current = selectedCard?.id;
    } else {
      setSubsRefetching(true);
    }
    const fetchData = async () => {
      try {
        const res1 = await fetch(`${REACT_APP_SERVER_URL}/all_workspace_data`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({
            work_id: selectedCard.id,
            page: subsPage,
            pageSize: subsPageSize,
            // Backend pins this sub to position 0 of page 1 (if present)
            // so a deep-link highlight target is always visible without
            // the admin needing to page through.
            pin_sub_id: highlightSubId,
          }),
        });

        if (res1.ok) {
          const data1 = await res1.json();
          // Never store undefined — the response may omit `data`, and the render
          // reads myAppData.length / .map directly, which white-screens on undefined.
          const freshRows = Array.isArray(data1.data) ? data1.data : [];
          setMyAppData(freshRows);
          setSubsTotal(
            Number.isFinite(data1.total) ? data1.total : data1.data?.length || 0,
          );

          // Re-point the open detail view at its FRESH row.
          //
          // selectedApp is a snapshot taken when the row was clicked, not a
          // live reference into myAppData. Without this, every in-page action
          // (save vendor cost, pay / void / accept an invoice, toggle sync)
          // refetched the list but left the detail page rendering the stale
          // object — so the new vendor cost or invoice status only appeared
          // after navigating out and back in.
          //
          // Re-pointing also gives selectedApp a new object identity, which
          // re-runs MyAppsDetail's own fetch effect (deps: [selectedApp]) and
          // reloads its invoices / payments / bills. One fix, both symptoms.
          setSelectedApp((prev) => {
            if (!prev?.id) return prev;
            const fresh = freshRows.find(
              (r) => Number(r?.id) === Number(prev.id),
            );
            // Sub vanished from this page (deleted, hidden, or paged away) —
            // keep what we have rather than blanking the open detail view.
            return fresh || prev;
          });

          // Seed local toggle state from server-returned hidden flag.
          const seeded = {};
          for (const sub of freshRows) seeded[sub.id] = !!sub.hidden_for_members;
          setHiddenById(seeded);
        }

        // Orders only need to load once per workspace — skip the extra
        // round-trip when we're just paginating subs on the same workspace.
        if (isFirstLoadForWorkspace) {
          const res2 = await fetch(`${REACT_APP_SERVER_URL}/all_order_data`, {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
            method: "POST",
            body: JSON.stringify({ work_id: selectedCard.id }),
          });

          if (res2.ok) {
            const data2 = await res2.json();
            // /all_order_data can come back without a `data` array; storing
            // undefined here is what crashed line 893 (myOrders.length) → white
            // screen on opening any workspace. Default to [].
            setMyOrders(Array.isArray(data2.data) ? data2.data : []);
          }
        }

        setPageLoaded(true);
        setSubsRefetching(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setSubsRefetching(false);
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [selectedCard, isSomethingChanged, subsPage, subsPageSize, highlightSubId]);

  // Ref to the DOM node of the highlighted sub row so we can scroll it
  // into view + play a brief flash animation once the data loads.
  const highlightedRowRef = React.useRef(null);
  const [highlightPulse, setHighlightPulse] = React.useState(false);

  React.useEffect(() => {
    if (!highlightSubId || !myAppData?.length) return;
    // Wait a tick so the row is actually mounted with its ref attached.
    const t = setTimeout(() => {
      if (highlightedRowRef.current) {
        try {
          highlightedRowRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        } catch (_) { /* ignore in older browsers */ }
      }
      setHighlightPulse(true);
      // Turn the pulse off after 4s so a page revisit doesn't re-flash
      // forever; the row keeps a subtle static outline via CSS.
      setTimeout(() => setHighlightPulse(false), 4000);
    }, 100);
    return () => clearTimeout(t);
  }, [highlightSubId, myAppData]);

  const clickHandler = (rowData) => {
    // All sub types open inline now (same model as recurring). The detail
    // component is picked at render time off the rowData shape. Previous
    // approach navigated to its own route, which unmounted MyAppsTable
    // and lost the selected workspace on back.
    setSelectedApp(rowData);
  };

  const payInvoice = () => {
    fetch(`${REACT_APP_SERVER_URL}/pay_invoice`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "POST",
      body: JSON.stringify({
        invoice_id: payId.invoice_id,
        regionId: payId.region === "IN" ? 1 : 0,
      }),
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
      })
      .then((data) => {
        if (data.status === 200) {
          setPayConfirmation(false);
          setIsSomethingChanged(!isSomethingChanged);
        } else {
          setErrorMessage(data.msg);
          setErrorSnackbarOpen(true);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Mark a Stripe invoice as void via the regional Stripe client. The
  const updateVendorRenewalDate = () => {
    // vendorRenewalDate is a YYYY-MM-DD string from <input type="date">;
    // empty string clears the field server-side.
    fetch(`${REACT_APP_SERVER_URL}/update_vendor_renewal_date`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "POST",
      body: JSON.stringify({
        vendor_renewal_date: vendorRenewalDate || null,
        id: selectdPS,
      }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.status === 200) {
          setEditVendorRenewalDate(false);
          setIsSomethingChanged(!isSomethingChanged);
        } else {
          setErrorMessage(data?.msg || "Failed to update vendor renewal date");
          setErrorSnackbarOpen(true);
        }
      })
      .catch((error) => console.log(error));
  };

  const updatePriceValidity = () => {
    // priceValidityDate is a YYYY-MM-DD string from <input type="date">;
    // empty string clears the field server-side.
    fetch(`${REACT_APP_SERVER_URL}/update_price_validity`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "POST",
      body: JSON.stringify({
        price_valid_until: priceValidityDate || null,
        id: selectdPS,
      }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.status === 200) {
          setEditPriceValidity(false);
          setIsSomethingChanged(!isSomethingChanged);
        } else {
          setErrorMessage(data?.msg || "Failed to update price validity date");
          setErrorSnackbarOpen(true);
        }
      })
      .catch((error) => console.log(error));
  };

  const updateProvider = () => {
    if (provider) {
      fetch(`${REACT_APP_SERVER_URL}/update_provider`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        method: "POST",
        body: JSON.stringify({
          provider: provider,
          id: selectdPS,
        }),
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
        })
        .then((data) => {
          if (data.status === 200) {
            setEditProvider(false);
            setIsSomethingChanged(!isSomethingChanged);
          } else {
            setErrorMessage(data.msg);
            setErrorSnackbarOpen(true);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      setErrorMessage("Enter provider");
      setErrorSnackbarOpen(true);
    }
  };

  return (
    <>
      <Dialog
        PaperProps={{
          style: { borderRadius: "20px" },
        }}
        open={payConfirmation}
      >
        <div
          style={{
            width: "500px",
            height: "200px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>
            <CloseIcon
              sx={{
                float: "right",
                color: "black",
                height: "4vh",
                mx: 2,
                mt: 3,
                cursor: "pointer",
              }}
              onClick={() => {
                setPayConfirmation(false);
              }}
            />
          </div>
          <DialogTitle id="alert-dialog-title" sx={{ mt: 0, pt: 0 }}>
            <p
              style={{ color: "#EF6D6D", fontWeight: "bold", fontSize: "20px" }}
            >
              Sure you want to mark as paid!! (Try to mark only if payment is
              recived)
            </p>
          </DialogTitle>
          <DialogActions sx={{ mb: 4, float: "right" }}>
            <Button
              onClick={() => setPayConfirmation(false)}
              variant="contained"
              sx={{ color: "#fff" }}
              color="error"
            >
              Cancel
            </Button>
            <Button
              onClick={() => payInvoice()}
              variant="contained"
              sx={{ color: "#fff" }}
              color="success"
            >
              Verify
            </Button>
          </DialogActions>
        </div>
      </Dialog>
      <Dialog
        PaperProps={{
          style: { borderRadius: "20px" },
        }}
        open={editProvider}
      >
        <div
          style={{
            width: "500px",
            height: "200px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>
            <CloseIcon
              sx={{
                float: "right",
                color: "black",
                height: "4vh",
                mx: 2,
                mt: 3,
                cursor: "pointer",
              }}
              onClick={() => {
                setEditProvider(false);
              }}
            />
          </div>
          <DialogTitle id="alert-dialog-title" sx={{ mt: 0, pt: 0 }}>
            <p
              style={{ color: "#EF6D6D", fontWeight: "bold", fontSize: "20px" }}
            >
              Update Provider
            </p>
          </DialogTitle>
          <FormControl sx={{ minWidth: 320 }} size="small">
            <InputLabel id="provider-select-label">Provider</InputLabel>
            <Select
              labelId="provider-select-label"
              label="Provider"
              value={
                providerOptions.some((p) => p.name === provider)
                  ? provider
                  : provider
                    ? provider
                    : ""
              }
              onChange={(e) => {
                if (e.target.value === CREATE_NEW_PROVIDER) {
                  setCreateProviderOpen(true);
                  return;
                }
                if (e.target.value === MANAGE_PROVIDERS) {
                  setManageProvidersOpen(true);
                  return;
                }
                setProvider(e.target.value);
              }}
              disabled={providerOptionsLoading}
            >
              {/* Preserve the current sub's existing value even if it
                  hasn't been backfilled into the providers table yet
                  (edge case: sub was edited before the migration ran). */}
              {provider && !providerOptions.some((p) => p.name === provider) && (
                <MenuItem value={provider}>{provider} (current)</MenuItem>
              )}
              {providerOptions.map((p) => (
                <MenuItem key={p.id} value={p.name}>
                  {p.name}
                </MenuItem>
              ))}
              <Divider />
              <MenuItem value={CREATE_NEW_PROVIDER} sx={{ color: "#134f8b", fontWeight: 600 }}>
                + Create new provider
              </MenuItem>
              <MenuItem value={MANAGE_PROVIDERS} sx={{ color: "#607084", fontWeight: 600 }}>
                ⚙ Manage providers (rename / merge typos)
              </MenuItem>
            </Select>
          </FormControl>
          <DialogActions sx={{ mb: 4, float: "right" }}>
            <Button
              onClick={() => setEditProvider(false)}
              variant="contained"
              sx={{ color: "#fff" }}
              color="error"
            >
              Cancel
            </Button>
            <Button
              onClick={() => updateProvider()}
              variant="contained"
              sx={{ color: "#fff" }}
              color="success"
            >
              Update
            </Button>
          </DialogActions>
        </div>
      </Dialog>

      {/* Create-new-provider mini dialog — stacks above the edit-provider dialog. */}
      <Dialog
        open={createProviderOpen}
        onClose={() => {
          if (!creatingProvider) {
            setCreateProviderOpen(false);
            setNewProviderName("");
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Create new provider</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Provider name"
            placeholder="e.g. Ingram Micro"
            value={newProviderName}
            onChange={(e) => setNewProviderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !creatingProvider) handleCreateProvider();
            }}
            helperText="Case-insensitive. If a provider with this name already exists, it will be reused."
            inputProps={{ maxLength: 120 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setCreateProviderOpen(false);
              setNewProviderName("");
            }}
            disabled={creatingProvider}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateProvider}
            disabled={creatingProvider || !newProviderName.trim()}
          >
            {creatingProvider ? "Creating…" : "Create & Select"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage providers — rename or delete rows to clean up typo duplicates
          (e.g. Redigton ↔ Redington). Renaming to a name that already exists
          MERGES both: every subscription on the old spelling gets rewritten
          to the canonical one, then the old row is deleted. */}
      <Dialog
        open={manageProvidersOpen}
        onClose={() => {
          setManageProvidersOpen(false);
          cancelRename();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Manage providers</DialogTitle>
        <DialogContent dividers>
          <p style={{ margin: "0 0 10px", fontSize: 13, color: "#607084" }}>
            Rename a provider to fix a typo. If the new name matches another
            provider in the list, they will be <b>merged</b> — every
            subscription on the old spelling will be updated to the surviving
            one, and the old row will be removed.
          </p>
          {providerOptionsLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {!providerOptionsLoading && providerOptions.length === 0 && (
            <Box sx={{ color: "#8696aa", py: 2, textAlign: "center", fontSize: 13 }}>
              No providers yet.
            </Box>
          )}
          {!providerOptionsLoading && providerOptions.length > 0 && (
            <Table size="small">
              <TableBody>
                {providerOptions.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ borderBottom: "1px solid #eef2f7" }}>
                      {renamingId === row.id ? (
                        <TextField
                          autoFocus
                          size="small"
                          fullWidth
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !renameBusy) submitRename();
                            if (e.key === "Escape") cancelRename();
                          }}
                          disabled={renameBusy}
                          inputProps={{ maxLength: 120 }}
                        />
                      ) : (
                        <span style={{ fontSize: 14, fontWeight: 500, color: "#1f2c3c" }}>
                          {row.name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: "1px solid #eef2f7", whiteSpace: "nowrap" }}>
                      {renamingId === row.id ? (
                        <>
                          <Button
                            size="sm"
                            variant="success"
                            onClick={submitRename}
                            disabled={renameBusy || !renameDraft.trim() || renameDraft.trim() === row.name}
                            style={{ marginRight: 6 }}
                          >
                            {renameBusy ? "Saving…" : "Save"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={cancelRename}
                            disabled={renameBusy}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => startRename(row)}
                            style={{ marginRight: 6 }}
                          >
                            Rename
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => deleteProviderRow(row)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setManageProvidersOpen(false);
              cancelRename();
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        PaperProps={{ style: { borderRadius: "20px" } }}
        open={editVendorRenewalDate}
      >
        <div
          style={{
            width: "500px",
            height: "220px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>
            <CloseIcon
              sx={{
                float: "right",
                color: "black",
                height: "4vh",
                mx: 2,
                mt: 3,
                cursor: "pointer",
              }}
              onClick={() => setEditVendorRenewalDate(false)}
            />
          </div>
          <DialogTitle id="vendor-renewal-dialog-title" sx={{ mt: 0, pt: 0 }}>
            <p
              style={{ color: "#EF6D6D", fontWeight: "bold", fontSize: "20px" }}
            >
              Set Vendor Renewal Date
            </p>
          </DialogTitle>
          <input
            type="date"
            value={vendorRenewalDate}
            onChange={(e) => setVendorRenewalDate(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
          <DialogActions sx={{ mb: 4, float: "right" }}>
            <Button
              onClick={() => setEditVendorRenewalDate(false)}
              variant="contained"
              sx={{ color: "#fff" }}
              color="error"
            >
              Cancel
            </Button>
            <Button
              onClick={() => updateVendorRenewalDate()}
              variant="contained"
              sx={{ color: "#fff" }}
              color="success"
            >
              Save
            </Button>
          </DialogActions>
        </div>
      </Dialog>
      <Dialog
        PaperProps={{ style: { borderRadius: "20px" } }}
        open={editPriceValidity}
      >
        <div
          style={{
            width: "500px",
            height: "220px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>
            <CloseIcon
              sx={{
                float: "right",
                color: "black",
                height: "4vh",
                mx: 2,
                mt: 3,
                cursor: "pointer",
              }}
              onClick={() => setEditPriceValidity(false)}
            />
          </div>
          <DialogTitle id="price-validity-dialog-title" sx={{ mt: 0, pt: 0 }}>
            <p
              style={{ color: "#EF6D6D", fontWeight: "bold", fontSize: "20px" }}
            >
              Set Price Validity Date
            </p>
          </DialogTitle>
          <input
            type="date"
            value={priceValidityDate}
            onChange={(e) => setPriceValidityDate(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
          <DialogActions sx={{ mb: 4, float: "right" }}>
            <Button
              onClick={() => setEditPriceValidity(false)}
              variant="contained"
              sx={{ color: "#fff" }}
              color="error"
            >
              Cancel
            </Button>
            <Button
              onClick={() => updatePriceValidity()}
              variant="contained"
              sx={{ color: "#fff" }}
              color="success"
            >
              Save
            </Button>
          </DialogActions>
        </div>
      </Dialog>
      {!pageLoaded && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
      {pageLoaded && (
        <>
          {Object.keys(selectedApp).length === 0 && (
            <>
              <div className={styles.toggleButtons}>
                <div className={styles.subscriptionToggle}>
                  <ToggleSwitch
                    nextOnlinePayment={selectedCard.nextOnlinePayment}
                    api={"online_toggle"}
                    id={selectedCard.id}
                    type={"workspace"}
                    isSomethingChanged={isSomethingChanged}
                    setIsSomethingChanged={setIsSomethingChanged}
                    setSelectedApp={setSelectedApp}
                    isSomethingChanged1={isSomethingChanged1}
                    setIsSomethingChanged1={setIsSomethingChanged1}
                  />
                </div>

                <div className={styles.zohoToggle}>
                  {!selectedCard.books_acc_id &&
                    (selectedCard.crm_acc_id ? (
                      <ToggleZohoSync
                        selectedCard={selectedCard}
                        setIsSomethingChanged={setIsSomethingChanged}
                      />
                    ) : (
                      "No Such Crm Account"
                    ))}

                  {selectedCard?.books_acc_id && <p>Synched With Zoho </p>}
                </div>
              </div>

              <div style={{ minHeight: "100px" }}>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <tbody>
                      <tr>
                        <td className={styles.cell}>
                          Workspace Name: {selectedCard.name}
                          {selectedCard?.id != null && (
                            <span
                              style={{
                                marginLeft: 8,
                                fontSize: "0.8em",
                                color: "#607084",
                                fontWeight: 500,
                              }}
                            >
                              · ID: {selectedCard.id}
                            </span>
                          )}
                        </td>
                        <td className={styles.cell}>
                          Owner Email: {selectedCard.owner_email}
                        </td>
                        <td className={styles.cell}>
                          Region:{" "}
                          {selectedCard.regionId === 1 ? "India" : "Global"}
                        </td>
                        <td className={styles.cell}>
                          Avg Savings(%): {selectedCard.avgSavings}
                        </td>
                      </tr>
                      <tr className={styles.stripedRow}>
                        <td className={styles.cell}>
                          Company: {selectedCard.company}
                        </td>
                        <td className={styles.cell}>
                          Address: {selectedCard.address?.split("|").join(" ")}
                        </td>
                        <td className={styles.cell}>
                          Tax Id: {selectedCard.tax_id}
                        </td>
                        <td className={styles.cell}>
                          Phone: {selectedCard.phone}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.cell}>
                          Payment Method: {selectedCard.payment_method}
                        </td>
                        <td className={styles.cell}>
                          Customer ID: {selectedCard.customerId}
                        </td>
                        <td className={styles.cell}>
                          Contact Id Zoho: {selectedCard.contactId}
                        </td>
                        <td className={styles.cell}>
                          Customer ID RZP:{selectedCard.customer_id_rzp}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* subs */}
              {subsTotal > 0 && (
                <Box
                  sx={{
                    position: "relative",
                    // Dim stale rows during a page refetch so the eye
                    // notices the change is in-flight; kept subtle so the
                    // table stays readable.
                    opacity: subsRefetching ? 0.55 : 1,
                    transition: "opacity 150ms ease",
                    pointerEvents: subsRefetching ? "none" : "auto",
                  }}
                >
                  {subsRefetching && (
                    <LinearProgress
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 2,
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                      }}
                    />
                  )}
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Subscription Name</StyledTableCell>
                          <StyledTableCell>Domain</StyledTableCell>
                          <StyledTableCell>Interval</StyledTableCell>
                          <StyledTableCell align="right">
                            Status
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            Renewal Date
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            Created Date
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            Vendor Renewal
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            Price Validity
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            Provider
                          </StyledTableCell>
                          {isSuperAdmin && (
                            <StyledTableCell align="right">
                              Hidden
                            </StyledTableCell>
                          )}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {myAppData.map((row, i) => {
                          const requiredFields = row.required_feilds;
                          const index = requiredFields.findIndex(
                            (obj) => obj.name === "domain"
                          );
                          const value =
                            index === -1 ? "" : requiredFields[index]["value"];
                          const isHighlighted =
                            highlightSubId && Number(row.id) === highlightSubId;

                          return (
                            <TableRow
                              key={i}
                              ref={isHighlighted ? highlightedRowRef : null}
                              sx={{
                                backgroundColor: isHighlighted
                                  ? highlightPulse
                                    ? "#fff7c2" // brighter pulse
                                    : "#fffbe6" // subtle static tint
                                  : "white",
                                transition: "background-color 900ms ease",
                                outline: isHighlighted
                                  ? "2px solid #f5c26b"
                                  : "none",
                                outlineOffset: isHighlighted ? "-2px" : 0,
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                                "&:hover": {
                                  backgroundColor: isHighlighted
                                    ? "#fff2a8"
                                    : "rgba(0, 0, 0, 0.1)",
                                  cursor: "pointer",
                                },
                                "&:focus": {
                                  outline: "none",
                                },
                                "&:active": {
                                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                                },
                                "&:focus-visible": {
                                  outline: "none",
                                },
                              }}
                            >
                              <TableCell
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clickHandler(row);
                                }}
                              >
                                {row.marketplaceName}
                              </TableCell>
                              <TableCell
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clickHandler(row);
                                }}
                              >
                                {value}
                              </TableCell>
                              <TableCell
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clickHandler(row);
                                }}
                              >
                                {row.n_interval} {row.interval}
                              </TableCell>
                              <TableCell
                                align="right"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clickHandler(row);
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor:
                                      statusData[row.status] || "#000",
                                    color: "white",
                                    fontFamily: "Poppins",
                                    fontSize: "0.7rem",
                                    padding: "2px 6px",
                                    borderRadius: "6px",
                                    border: statusData[row.status] || "#000",
                                    marginTop: "0.5rem",
                                    marginLeft: "1.5rem",
                                    marginBottom: "0.5rem",
                                    transition:
                                      "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                                    display: "inline-block",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {row.status}
                                </div>
                                {/* Cancelled / cancel_next but a bill is still
                                    unpaid — show overdue beside the cancel
                                    status so both are visible. */}
                                {row.overdue_after_cancel && (
                                  <div
                                    title="Cancelled, but a bill on this subscription is still unpaid"
                                    style={{
                                      backgroundColor:
                                        statusData.overdue || "#f11b22",
                                      color: "white",
                                      fontFamily: "Poppins",
                                      fontSize: "0.7rem",
                                      padding: "2px 6px",
                                      borderRadius: "6px",
                                      marginLeft: "0.4rem",
                                      display: "inline-block",
                                      textAlign: "center",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    overdue
                                  </div>
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clickHandler(row);
                                }}
                              >
                                {row.dueDate
                                  ?.split("T")[0]
                                  .split("-")
                                  .reverse()
                                  .join("-")}
                              </TableCell>
                              <TableCell
                                align="right"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clickHandler(row);
                                }}
                              >
                                {row.createdAt
                                  .split("T")[0]
                                  .split("-")
                                  .reverse()
                                  .join("-")}
                              </TableCell>
                              <TableCell align="right">
                                {row.vendor_renewal_date
                                  ? String(row.vendor_renewal_date)
                                      .split("T")[0]
                                      .split("-")
                                      .reverse()
                                      .join("-")
                                  : "--"}
                                <button
                                  style={{
                                    marginLeft: "2%",
                                    height: "20px",
                                    width: "20px",
                                    padding: "0%",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    // Seed the input with the existing date
                                    // (or empty) so the picker opens on it.
                                    setVendorRenewalDate(
                                      row.vendor_renewal_date
                                        ? String(row.vendor_renewal_date).split(
                                            "T",
                                          )[0]
                                        : "",
                                    );
                                    setSelectedPS(row.id);
                                    setEditVendorRenewalDate(true);
                                  }}
                                >
                                  <Edit
                                    sx={{
                                      height: "10px",
                                      width: "10px",
                                      color: "blue",
                                    }}
                                  />
                                </button>
                              </TableCell>
                              <TableCell align="right">
                                {row.price_valid_until
                                  ? String(row.price_valid_until)
                                      .split("T")[0]
                                      .split("-")
                                      .reverse()
                                      .join("-")
                                  : "--"}
                                <button
                                  style={{
                                    marginLeft: "2%",
                                    height: "20px",
                                    width: "20px",
                                    padding: "0%",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setPriceValidityDate(
                                      row.price_valid_until
                                        ? String(row.price_valid_until).split(
                                            "T",
                                          )[0]
                                        : "",
                                    );
                                    setSelectedPS(row.id);
                                    setEditPriceValidity(true);
                                  }}
                                >
                                  <Edit
                                    sx={{
                                      height: "10px",
                                      width: "10px",
                                      color: "blue",
                                    }}
                                  />
                                </button>
                              </TableCell>
                              <TableCell align="right">
                                {row.provider ? row.provider : "--"}
                                <button
                                  style={{
                                    marginLeft: "2%",
                                    height: "20px",
                                    width: "20px",
                                    padding: "0%",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    // Seed the Select with this sub's current
                                    // provider so it opens pre-selected rather
                                    // than blank.
                                    setProvider(String(row.provider || ""));
                                    setEditProvider(true);
                                    setSelectedPS(row.id);
                                  }}
                                >
                                  <Edit
                                    sx={{
                                      height: "10px",
                                      width: "10px",
                                      color: "blue",
                                    }}
                                  />
                                </button>
                              </TableCell>
                              {isSuperAdmin && (
                                <TableCell align="right">
                                  <Switch
                                    checked={!!hiddenById[row.id]}
                                    onChange={(e) =>
                                      toggleSubHidden(row.id, e.target.checked)
                                    }
                                    color="warning"
                                    size="small"
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={subsTotal}
                    // MUI TablePagination is 0-indexed; our URL is 1-indexed.
                    page={Math.max(0, subsPage - 1)}
                    onPageChange={(_, newZeroPage) =>
                      patchSubsParams({ subsPage: newZeroPage + 1 })
                    }
                    rowsPerPage={subsPageSize}
                    rowsPerPageOptions={ALLOWED_PAGE_SIZES}
                    onRowsPerPageChange={(e) => {
                      // Reset to page 1 when page size changes so we don't
                      // land on an out-of-range page after enlarging.
                      patchSubsParams({
                        subsPageSize: parseInt(e.target.value, 10),
                        subsPage: 1,
                      });
                    }}
                    labelRowsPerPage="Subscriptions per page:"
                    // Guard against double-clicks / spam page changes while
                    // the current fetch is still in flight — prevents piling
                    // up Stripe calls we'd throw away.
                    backIconButtonProps={{ disabled: subsRefetching }}
                    nextIconButtonProps={{ disabled: subsRefetching }}
                    SelectProps={{ disabled: subsRefetching }}
                  />
                </Box>
              )}

              {/* orders */}
              {myOrders.length > 0 && (
                <>
                  <TableContainer component={Paper} sx={{ mt: 7 }}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Order Name</StyledTableCell>
                          <StyledTableCell>Domain</StyledTableCell>
                          <StyledTableCell align="right">
                            Status
                          </StyledTableCell>
                          <StyledTableCell>Quantity</StyledTableCell>
                          <StyledTableCell align="right">
                            Total Amount
                          </StyledTableCell>
                          <StyledTableCell align="right">Type</StyledTableCell>
                          <StyledTableCell align="right">
                            Created Date
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            Action
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {myOrders.map((row, i) => {
                          const requiredFields = row.requiredFeilds;
                          const index = requiredFields?.findIndex(
                            (obj) => obj.name === "domain"
                          );
                          const value = requiredFields
                            ? index === -1
                              ? ""
                              : requiredFields[index]["value"]
                            : "";

                          return (
                            <TableRow
                              key={i}
                              sx={{
                                backgroundColor: "white",
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                                "&:focus": {
                                  outline: "none",
                                },
                                "&:active": {
                                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                                },
                                "&:focus-visible": {
                                  outline: "none",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <TableCell>{row.marketplaceName}</TableCell>
                              <TableCell>{value}</TableCell>
                              <TableCell align="right">
                                <div
                                  style={{
                                    backgroundColor:
                                      statusData[row.status] || "#000",
                                    color: "white",
                                    fontFamily: "Poppins",
                                    fontSize: "0.7rem",
                                    padding: "2px 6px",
                                    borderRadius: "6px",
                                    border: statusData[row.status] || "#000",
                                    marginTop: "0.5rem",
                                    marginLeft: "1.5rem",
                                    marginBottom: "0.5rem",
                                    transition:
                                      "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                                    display: "inline-block",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {row.status}
                                </div>
                              </TableCell>
                              <TableCell align="right">
                                {row.quantity}
                              </TableCell>
                              <TableCell align="right">
                                {row.subtotal_default} {row.currency}
                              </TableCell>
                              <TableCell align="right">{row.type}</TableCell>
                              <TableCell align="right">
                                {row.createdAt
                                  .split("T")[0]
                                  .split("-")
                                  .reverse()
                                  .join("-")}
                              </TableCell>
                              <TableCell align="right">
                                {row.status === "processing" &&
                                  row.type === "New" && (
                                    <button
                                      onClick={() => {
                                        setPayId({
                                          invoice_id: row.invoice_id,
                                          region: row.region,
                                        });
                                        setPayConfirmation(true);
                                      }}
                                    >
                                      Pay
                                    </button>
                                  )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* users */}

              <>
                <TableContainer component={Paper} sx={{ mt: 7 }}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Company </StyledTableCell>
                        <StyledTableCell>Name</StyledTableCell>
                        <StyledTableCell>Email</StyledTableCell>
                        <StyledTableCell>Role</StyledTableCell>
                        <StyledTableCell align="right">Status</StyledTableCell>
                        <StyledTableCell align="right">
                          Renewal Date
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          Created Date
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {selectedCard.workspace_users.map((row, i) => {
                        return (
                          <TableRow
                            key={i}
                            sx={{
                              backgroundColor: "white",
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.1)",
                                cursor: "pointer",
                              },
                              "&:focus": {
                                outline: "none",
                              },
                              "&:active": {
                                backgroundColor: "rgba(0, 0, 0, 0.1)",
                              },
                              "&:focus-visible": {
                                outline: "none",
                              },
                            }}
                          >
                            <TableCell>{row.company}</TableCell>
                            <TableCell>
                              {row.firstName}
                              {""} {row.lastName}
                            </TableCell>
                            <TableCell>{row.email}</TableCell>
                            <TableCell>{row.workspace_role}</TableCell>
                            <TableCell align="right">
                              <div
                                style={{
                                  backgroundColor:
                                    statusData[row.status] || "#000",
                                  color: "white",
                                  fontFamily: "Poppins",
                                  fontSize: "0.7rem",
                                  padding: "2px 6px",
                                  borderRadius: "6px",
                                  border: statusData[row.status] || "#000",
                                  marginTop: "0.5rem",
                                  marginLeft: "1.5rem",
                                  marginBottom: "0.5rem",
                                  transition:
                                    "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                                  display: "inline-block",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                }}
                              >
                                {row.status}
                              </div>
                            </TableCell>
                            <TableCell align="right">
                              {row.updatedAt
                                .split("T")[0]
                                .split("-")
                                .reverse()
                                .join("-")}
                            </TableCell>
                            <TableCell align="right">
                              {row.createdAt
                                .split("T")[0]
                                .split("-")
                                .reverse()
                                .join("-")}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            </>
          )}

          {Object.keys(selectedApp).length !== 0 && (
            <>
              {/* Refetch indicator for the detail view. The subs-table dim +
                  progress bar isn't visible from in here, so without this an
                  action like "pay invoice" or "save vendor cost" looked like
                  nothing happened for the 1-3s the refetch takes. */}
              {subsRefetching ? (
                <Box sx={{ position: "relative", mb: 1 }}>
                  <LinearProgress
                    sx={{ borderRadius: 1, height: 3 }}
                  />
                  <Box
                    sx={{
                      fontSize: 12,
                      color: "#607084",
                      mt: 0.75,
                      fontWeight: 500,
                    }}
                  >
                    Refreshing subscription…
                  </Box>
                </Box>
              ) : null}
              {selectedApp.marketplaceName === "Easexpense Domains" ? (
                <DomainDetail
                  selectedApp={selectedApp}
                  setSelectedApp={setSelectedApp}
                  isSomethingChanged={isSomethingChanged}
                  setIsSomethingChanged={setIsSomethingChanged}
                />
              ) : selectedApp.type === "one_time" ? (
                <MyAppsOnetimeDetail
                  selectedApp={selectedApp}
                  setSelectedApp={setSelectedApp}
                  isSomethingChanged={isSomethingChanged}
                  setIsSomethingChanged={setIsSomethingChanged}
                />
              ) : (
                <MyAppsDetail
                  selectedApp={selectedApp}
                  setSelectedApp={setSelectedApp}
                  isSomethingChanged={isSomethingChanged}
                  setIsSomethingChanged={setIsSomethingChanged}
                />
              )}
            </>
          )}
        </>
      )}

      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleErrorSnackbarClose}
      >
        <Alert
          onClose={handleErrorSnackbarClose}
          variant="filled"
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MyAppsFunctions;
